import React from 'react';
import { Grid, Card, CardContent, Typography, Box, Container } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import {
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Compare as CompareIcon,
  Whatshot as WhatshotIcon,
  Functions as FunctionsIcon,
  Group as GroupIcon,
  Troubleshoot as TroubleshootIcon,
  AssuredWorkload as AssuredWorkloadIcon,
  CalendarToday as CalendarTodayIcon,
  Event as EventIcon,
  Newspaper as NewspaperIcon,
} from '@mui/icons-material';

// Professional Action Card with glassmorphism styling
const ActionCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  textDecoration: 'none',
  color: 'inherit',
  height: '100px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  transition: 'all 0.2s ease-in-out',

  // Match your existing card styling from theme.js
  backgroundImage: theme.palette.mode === 'dark'
    ? 'linear-gradient(145deg, rgba(19, 47, 76, 0.4), rgba(19, 47, 76, 0.1))'
    : 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255, 255, 255, 0.1)'
    : '1px solid rgba(0, 0, 0, 0.05)',

  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 8px 24px rgba(0,0,0,0.2)'
      : '0 8px 24px rgba(0,0,0,0.1)',
  },
}));

// Refined icon styling - monochromatic and professional
const ActionIcon = styled(Box)(({ theme }) => ({
  width: '32px',
  height: '32px',
  marginBottom: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.8)'
    : theme.palette.text.primary,

  '& svg': {
    fontSize: '24px',
  }
}));

// Section styling
const SectionContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: 600,
  color: theme.palette.mode === 'dark'
    ? 'rgba(255, 255, 255, 0.9)'
    : theme.palette.text.primary,
  textAlign: 'center',
}));

// Actions list
const actions = [
  { icon: <AccountBalanceWalletIcon />, title: 'Portfolio', link: '/portfolio' },
  { icon: <CompareIcon />, title: 'Asset Comparison', link: '/asset-comparison' },
  { icon: <AssuredWorkloadIcon />, title: 'POTUS Tracker', link: '/potus-tracker' },
  { icon: <FunctionsIcon />, title: 'Screener', link: '/screener?exchange=AMEX%2CNASDAQ%2CNYSE' },
  { icon: <GroupIcon />, title: 'Congress Members', link: '/congress-members' },
  { icon: <TroubleshootIcon />, title: 'Insider Tracker', link: '/insider-tracker' },
  { icon: <WhatshotIcon />, title: 'Market Movers', link: '/market-movers' },
  { icon: <CalendarTodayIcon />, title: 'Earnings Calendar', link: '/earnings-calendar' },
  { icon: <EventIcon />, title: 'Economic Calendar', link: '/economic-calendar' },
  { icon: <NewspaperIcon />, title: 'Market News', link: '/News' },
];

const DashboardActionGrid = () => {
  return (
    <SectionContainer>
      <Container maxWidth="lg">
        <SectionHeader variant="h6" component="h2">
          Quick Actions
        </SectionHeader>

        <Grid container spacing={2}>
          {actions.map((action, index) => (
            <Grid
              item
              key={index}
              xs={6}   // 2 per row on mobile
              sm={4}   // 3 per row on tablet
              md={2.4} // 5 per row on desktop (12/5 = 2.4)
            >
              <ActionCard
                component={RouterLink}
                to={action.link}
                elevation={0}
              >
                <CardContent
                  sx={{
                    textAlign: 'center',
                    p: 1.5,
                    '&:last-child': { pb: 1.5 },
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                  }}
                >
                  <ActionIcon>
                    {action.icon}
                  </ActionIcon>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      lineHeight: 1.2,
                      textAlign: 'center',
                    }}
                  >
                    {action.title}
                  </Typography>
                </CardContent>
              </ActionCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </SectionContainer>
  );
};

export default DashboardActionGrid;